'use client'

import { createContext, useContext, useMemo } from 'react'
import { LOCALE_COOKIE_NAME, Locale, normalizeLocale } from '@/lib/i18n/config'
import { t } from '@/lib/i18n/messages'

type LocaleContextValue = {
  locale: Locale
  t: (key: string, fallback?: string) => string
  setLocale: (nextLocale: Locale) => void
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

export function LocaleProvider({
  locale,
  children,
}: {
  locale: Locale
  children: React.ReactNode
}) {
  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      t: (key, fallback) => t(locale, key, fallback),
      setLocale: (nextLocale) => {
        const normalized = normalizeLocale(nextLocale)
        const secure = window.location.protocol === 'https:' ? '; Secure' : ''
        document.cookie = `${LOCALE_COOKIE_NAME}=${normalized}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`
        localStorage.setItem(LOCALE_COOKIE_NAME, normalized)
      },
    }),
    [locale],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider')
  }
  return context
}

